import { Shield, Heart, Crown, Users, Sword, Footprints } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';

const TYPE_CONFIG = {
  boss: { color: 'text-red-500', border: 'border-red-900', bg: 'from-red-950/50' },
  enemy: { color: 'text-red-400', border: 'border-red-800/50', bg: 'from-red-900/30' },
  friendly: { color: 'text-green-500', border: 'border-green-900/50', bg: 'from-green-950/30' },
  neutral: { color: 'text-stone-400', border: 'border-stone-700', bg: 'from-stone-800/30' },
  npc: { color: 'text-amber-500', border: 'border-amber-900/50', bg: 'from-amber-950/30' },
  merchant: { color: 'text-yellow-500', border: 'border-yellow-900/50', bg: 'from-yellow-950/30' },
  companion: { color: 'text-blue-500', border: 'border-blue-900/50', bg: 'from-blue-950/30' },
};

export default function CharacterPreview({ character }) {
  const config = TYPE_CONFIG[character.type] || TYPE_CONFIG.npc;
  
  const hpPercent = character.stats?.hp?.max 
    ? (character.stats.hp.current / character.stats.hp.max) * 100 
    : 100;
  const hpClass = hpPercent <= 25 ? 'bg-red-600' : hpPercent <= 50 ? 'bg-yellow-600' : 'bg-green-600';

  const getModifier = (score) => {
    if (!score) return '+0';
    const mod = Math.floor((score - 10) / 2);
    return mod >= 0 ? `+${mod}` : `${mod}`;
  };

  return (
    <div className={`bg-gradient-to-b ${config.bg} to-stone-900 border ${config.border} rounded-lg overflow-hidden`} data-testid="character-preview">
      {/* Image */}
      <div className="aspect-video relative overflow-hidden bg-stone-800">
        {character.image ? (
          <img src={character.image} alt={character.name} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Crown className={`w-20 h-20 ${config.color} opacity-30`} />
          </div>
        )}
        <div className={`absolute top-3 right-3 px-3 py-1 rounded font-cinzel text-sm uppercase ${config.color} bg-black/70 backdrop-blur`}>
          {character.type || 'NPC'}
        </div>
        {/* HP Bar overlay */}
        {character.stats?.hp?.max > 0 && (
          <div className="absolute bottom-0 left-0 right-0 h-3 bg-black/60">
            <div className={`h-full ${hpClass} transition-all`} style={{ width: `${hpPercent}%` }} />
            <div className="absolute inset-0 flex items-center justify-center text-xs font-cinzel text-white text-shadow">
              {character.stats.hp.current}/{character.stats.hp.max}
            </div>
          </div>
        )}
      </div>

      {/* Content */}
      <div className="p-4 space-y-4">
        {/* Name & Alignment */}
        <div>
          <h3 className="font-cinzel text-xl font-bold text-stone-100">
            {character.name || 'Unnamed Character'}
          </h3>
          {character.alignment && (
            <span className="text-xs text-stone-500 uppercase tracking-wider">{character.alignment}</span>
          )}
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-3">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-2 px-3 py-2 bg-stone-800/50 rounded border border-stone-700">
                  <Heart className="w-4 h-4 text-red-500" />
                  <span className="font-cinzel text-stone-200">
                    {character.stats?.hp?.current || 0}/{character.stats?.hp?.max || 0}
                  </span>
                </div>
              </TooltipTrigger>
              <TooltipContent>Hit Points</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-2 px-3 py-2 bg-stone-800/50 rounded border border-stone-700">
                  <Shield className="w-4 h-4 text-blue-500" />
                  <span className="font-cinzel text-stone-200">{character.stats?.ac || 0}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent>Armor Class</TooltipContent>
            </Tooltip>
          </TooltipProvider>

          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <div className="flex items-center gap-2 px-3 py-2 bg-stone-800/50 rounded border border-stone-700">
                  <Footprints className="w-4 h-4 text-green-500" />
                  <span className="font-cinzel text-stone-200">{character.stats?.speed || 0}</span>
                </div>
              </TooltipTrigger>
              <TooltipContent>Speed</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>

        {/* Ability Scores */}
        <div className="grid grid-cols-6 gap-2">
          {['strength', 'dexterity', 'constitution', 'intelligence', 'wisdom', 'charisma'].map(stat => (
            <TooltipProvider key={stat}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className="text-center p-2 bg-stone-800/30 rounded border border-stone-700/50">
                    <div className="font-cinzel text-xs text-stone-500 uppercase">{stat.slice(0, 3)}</div>
                    <div className="font-cinzel text-lg text-stone-200">{character.stats?.[stat] || 10}</div>
                    <div className="text-xs text-amber-500">{getModifier(character.stats?.[stat])}</div>
                  </div>
                </TooltipTrigger>
                <TooltipContent className="capitalize">{stat}</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ))}
        </div>

        {/* Traits */}
        {character.traits && Object.keys(character.traits).length > 0 && (
          <div className="space-y-2">
            <h4 className="font-cinzel text-xs text-stone-500 uppercase tracking-wider">Traits</h4>
            <div className="flex flex-wrap gap-2">
              {Object.entries(character.traits).slice(0, 5).map(([key, value]) => (
                <span key={key} className="px-2 py-1 bg-stone-800 rounded text-xs text-stone-400">
                  {key}: {Array.isArray(value) ? value.join(', ') : String(value)}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Actions preview */}
        {character.actions && character.actions.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-cinzel text-xs text-stone-500 uppercase tracking-wider flex items-center gap-2">
              <Sword className="w-3 h-3" />
              Actions ({character.actions.length})
            </h4>
            <div className="space-y-1">
              {character.actions.slice(0, 3).map((action, i) => (
                <div key={i} className="text-sm text-stone-400 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
                  {action.name} {action.damage?.dice && <span className="text-xs text-stone-500">({action.damage.dice})</span>}
                </div>
              ))}
              {character.actions.length > 3 && (
                <div className="text-xs text-stone-500">+{character.actions.length - 3} more</div>
              )}
            </div>
          </div>
        )}

        {/* Minions preview */}
        {character.children && character.children.length > 0 && (
          <div className="space-y-2">
            <h4 className="font-cinzel text-xs text-stone-500 uppercase tracking-wider flex items-center gap-2">
              <Users className="w-3 h-3" />
              Minions ({character.children.length})
            </h4>
            <div className="flex flex-wrap gap-2">
              {character.children.map((child, i) => (
                <span key={i} className="px-2 py-1 bg-stone-800 rounded text-xs text-stone-400">
                  {child.name || 'Unnamed'}
                </span>
              ))}
            </div>
          </div>
        )}

        {/* Description preview */}
        {character.description && (
          <div className="pt-3 border-t border-stone-800">
            <p className="text-sm text-stone-400 line-clamp-3">{character.description}</p>
          </div>
        )}
      </div>
    </div>
  );
}
